import { Component, OnDestroy, signal } from '@angular/core';
import { DialogService } from '../../../services/dialog/dialog.service';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KeyValueTableService } from '../../../reusable-components/key-value-table/key-value-table.service';
import {
  KeyValue,
  KeyValueTableComponent,
} from '../../../reusable-components/key-value-table/key-value-table.component';
import { FooterService } from '../../../reusable-components/footer/footer.service';
import { MatStepperService } from '../lead-task-services/mat-stepper.service';
import { ReplaySubject, Subscription, Unsubscribable } from 'rxjs';
import { LeadTaskService } from '../lead-task.service';
import {
  getLeadCategoryWithEmoji,
  getSortedLeadCategories,
  LeadCategory,
  OpeningTimes,
} from '../../../lead.model';
import { MatIcon } from '@angular/material/icon';

import { MatCard, MatCardContent } from '@angular/material/card';
import { CompressedOpeningTimes } from '../../../reusable-components/compressed-opening-times';
import { getCountryCallingCode } from 'libphonenumber-js';
import {
  currentExaminationSettingsKey,
  SettingsService,
} from '../../../services/indexed-db/object-stores/settings/settings.service';
import { LeadDashboardService } from '../../lead-dashboard.service';
import { ErrorListService } from '../../../reusable-components/error-list/error-list.service';
import { ErrorListComponent } from '../../../reusable-components/error-list/error-list.component';

interface Room {
  name: string;
  color: string;
  icon: string;
}

@Component({
  selector: 'app-detail-information',
  imports: [
    KeyValueTableComponent,
    MatIcon,
    TranslatePipe,
    MatCard,
    MatCardContent,
    ErrorListComponent,
  ],
  templateUrl: './detail-information.component.html',
  styleUrl: './detail-information.component.scss',
  providers: [
    {
      provide: ErrorListService,
      useFactory: (leadTaskService: LeadTaskService) =>
        leadTaskService.errorListServiceDetailInformation,
      deps: [LeadTaskService],
    },
  ],
})
export class DetailInformationComponent implements OnDestroy {
  private readonly leadCategoryNeededError =
    'components.detail-information.lead-category-needed-error';
  active = new ReplaySubject<void>(1);
  active$ = this.active.asObservable();

  rooms = signal<Room[]>([]);
  selfCreatedRooms = signal<Room[]>([]);

  compressedOpeningTimesSubscription: Unsubscribable | undefined;
  matStepperSubscription: Subscription | undefined;
  footerButtonsSubscription: Subscription | undefined;
  roomsStatusSubscription: Subscription | undefined;
  keyValues: KeyValue[] = [];

  constructor(
    private readonly leadTaskService: LeadTaskService,
    private readonly dialogService: DialogService,
    private readonly snackbarService: SnackbarService,
    private readonly translateService: TranslateService,
    private readonly footerService: FooterService,
    private readonly matStepperService: MatStepperService,
    private readonly leadDashboardService: LeadDashboardService,
    private readonly settingsService: SettingsService,
  ) {
    this.setKeyValues();
    this.setActiveObservable();
    this.setFooterButtonsSubscription();
    this.setRoomsObservable();
    this.updateLeadCategoryError();
  }

  // LEER is only a placeholder - block progressing until a real category has been picked.
  private updateLeadCategoryError(): void {
    if (this.leadTaskService.getLeadCategory() === 'LEER') {
      this.leadTaskService.errorListServiceDetailInformation.addError(
        this.leadCategoryNeededError,
      );
    } else {
      this.leadTaskService.errorListServiceDetailInformation.removeError(
        this.leadCategoryNeededError,
      );
    }
  }

  setRoomsObservable() {
    this.roomsStatusSubscription = this.active$.subscribe(() => {
      const currentid = this.leadTaskService.getCurrentRoomExaminationId();
      const mapIdToRoom = (id: string) => {
        if (currentid === id) {
          return {
            name: this.leadTaskService.getUsageSpaceName(id),
            color: 'orange',
            icon: 'build',
          };
        }
        const examined = this.leadTaskService.hasRoomDraft(id);
        return {
          name: this.leadTaskService.getUsageSpaceName(id),
          color: examined ? 'green' : 'red',
          icon: examined ? 'done' : 'close',
        };
      };
      this.rooms.set(this.leadTaskService.getUsageSpaceIds().map(mapIdToRoom));
      this.selfCreatedRooms.set(
        this.leadTaskService.getSelfCreatedRoomsIds().map(mapIdToRoom),
      );
    });
  }

  setActiveObservable() {
    this.matStepperSubscription = this.matStepperService.currentStep$.subscribe(
      (index: number) => {
        if (index === 0) {
          this.active.next();
        }
      },
    );
  }

  setFooterButtonsSubscription() {
    this.footerButtonsSubscription = this.active$.subscribe(() => {
      this.footerService.setLeftButton({
        label: 'components.lead-task.abort',
        click: () => this.abortEditing(),
      });
      this.footerService.setRightButton(this.matStepperService.getNextButton());
    });
  }

  abortEditing() {
    const yes = async () => {
      this.leadDashboardService.finishExamination();
      this.settingsService.deleteSetting(currentExaminationSettingsKey);
    };
    const no = () => {
      return;
    };
    this.dialogService.openYesNoDialog(
      'components.lead-task.abort-text',
      'components.lead-task.abort-yes',
      'components.lead-task.abort-no',
      yes,
      no,
    );
  }

  setKeyValues() {
    const keyValueTableService = KeyValueTableService.create(
      this.translateService,
      this.dialogService,
      this.snackbarService,
    );

    this.keyValues = [
      keyValueTableService.createUpdateKeyValuePair({
        key: 'components.usage-spaces.basic-info.name',
        updateValueFn: (value: string) => this.leadTaskService.setName(value),
        getValueFn: () => this.leadTaskService.getName(),
        acceptInputFn: (value: string) =>
          this.leadTaskService.validateTwoPartSAPField(value),
        errorMessage: 'components.detail-information.too-long-error-80',
      }),
      keyValueTableService.createUpdateKeyValuePair({
        key: 'components.usage-spaces.basic-info.tel',
        updateValueFn: (value: string) => this.leadTaskService.setTel(value),
        getValueFn: () => this.leadTaskService.getTel(),
        acceptInputFn: (value: string) =>
          this.leadTaskService.validateTel(value),
        errorMessage: 'components.detail-information.tel-error',
        type: 'tel',
        valuePrefix: '+' + getCountryCallingCode('DE'),
        formatter: (value: string) =>
          this.leadTaskService.formatPhoneNumber(value),
      }),
      keyValueTableService.createUpdateKeyValuePair({
        key: 'components.usage-spaces.basic-info.website',
        updateValueFn: (value: string) =>
          this.leadTaskService.setWebsite(value.toLowerCase()),
        getValueFn: () => this.leadTaskService.getWebsite(),
        acceptInputFn: (value: string) =>
          this.leadTaskService.validateWebsite(value.toLowerCase()),
        errorMessage: 'components.detail-information.website-error',
        type: 'text',
        cellClickFn: () => {
          const website = this.leadTaskService.getWebsite();
          if (URL.canParse(website)) {
            window.open(new URL(website).href, '_blank');
          } else {
            window.open('https://' + website, '_blank');
          }
        },
      }),
      keyValueTableService.createKeyTranslateValuePair(
        'components.usage-spaces.basic-info.address',
        this.leadTaskService.getAddress(),
      ),
      this.getOpeningTimeDialogKeyValuePair(),
      this.getLeadCategoryKeyValuePair(),
      keyValueTableService.createUpdateKeyValuePair({
        key: 'components.usage-spaces.basic-info.operator-name',
        updateValueFn: (value: string) =>
          this.leadTaskService.setOperatorName(value),
        getValueFn: () => this.leadTaskService.getOperatorName(),
        acceptInputFn: (value: string) =>
          this.leadTaskService.validateTwoPartSAPField(value),
        errorMessage: 'components.detail-information.too-long-error-80',
      }),
    ];
  }

  getLeadCategoryKeyValuePair(): KeyValue {
    const keyValue: KeyValue = {
      key: 'components.usage-spaces.basic-info.leadCategory',
      value: getLeadCategoryWithEmoji(
        this.leadTaskService.getLeadCategory(),
        this.translateService,
      ),
      tryUpdate: () => {
        const onSelect = (option: string) => {
          this.leadTaskService.setLeadCategory(option as LeadCategory);
          keyValue.value = getLeadCategoryWithEmoji(
            option as LeadCategory,
            this.translateService,
          );
          this.updateLeadCategoryError();
          this.snackbarService.show('components.input-dialog.snackbar');
        };
        this.dialogService.openSelectDialog(
          getSortedLeadCategories(this.translateService).map(
            (leadCategory: LeadCategory) => {
              return {
                option: leadCategory,
                label: getLeadCategoryWithEmoji(leadCategory, this.translateService),
              };
            },
          ),
          'components.usage-spaces.basic-info.leadCategory',
          'components.detail-information.lead-category-question',
          onSelect,
          undefined,
          this.leadTaskService.getLeadCategory(),
        );
      },
      translateValue: false,
    };
    return keyValue;
  }

  getOpeningTimeDialogKeyValuePair(): KeyValue {
    const compressedOpeningTimes = new CompressedOpeningTimes(
      this.translateService,
      this.leadTaskService.getOpeningTimes(),
    );
    const keyValue = {
      key: 'components.usage-spaces.basic-info.openingTimes',
      value: '',
      translateValue: true,
      tryUpdate: () => {
        this.dialogService.openPickOpeningTimesDialog({
          initialOpeningTimes: this.leadTaskService.getOpeningTimes(),
          callback: (openingTimes: OpeningTimes) => {
            this.leadTaskService.setOpeningTimes(openingTimes);
            compressedOpeningTimes.setNewOpeningTimes(openingTimes);
          },
        });
      },
    };

    this.compressedOpeningTimesSubscription = compressedOpeningTimes.subscribe(
      (newCompressedOpeningTimes: string) =>
        (keyValue.value = newCompressedOpeningTimes),
    );
    return keyValue;
  }

  ngOnDestroy(): void {
    this.matStepperSubscription?.unsubscribe();
    this.footerButtonsSubscription?.unsubscribe();
    this.roomsStatusSubscription?.unsubscribe();
    this.compressedOpeningTimesSubscription?.unsubscribe();
  }
}
